import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/apiMode';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, User } from 'lucide-react';

export default function PartnerClients() {
  const { token } = useApp();
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/api/partner/clients');
      setClients(r.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore caricamento clienti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', email: '', company: '', phone: '' });
    setShowForm(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name, email: client.email || '', company: client.company || '', phone: client.phone || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/partner/clients/${editing.id}`, form);
      } else {
        await api.post('/api/partner/clients', form);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(t('partner.clients.confirmDelete'))) return;
    try {
      await api.delete(`/api/partner/clients/${client.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore eliminazione');
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.5rem', fontWeight: 800 }}>
            {t('partner.clients.title')}
          </h1>
          <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            {t('partner.clients.subtitle')}
          </p>
        </div>
        <button
          onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem' }}
        >
          <Plus size={16} /> {t('partner.clients.addClient')}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 1.25rem', color: '#1e3a8a', fontSize: '1.1rem' }}>
              {editing ? t('partner.clients.editClient') : t('partner.clients.addClient')}
            </h2>
            {[
              { key: 'name', label: t('partner.clients.name'), required: true, placeholder: 'Mario Rossi' },
              { key: 'email', label: t('partner.clients.email'), required: false, placeholder: 'mario@example.com' },
              { key: 'company', label: t('partner.clients.company'), required: false, placeholder: 'Rossi Srl' },
              { key: 'phone', label: t('partner.clients.phone'), required: false, placeholder: t('partner.clients.phonePlaceholder') },
            ].map(({ key, label, required, placeholder }) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.3rem' }}>
                  {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
                </label>
                <input
                  type="text"
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontWeight: 600, cursor: 'pointer' }}>
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                style={{ padding: '0.55rem 1.25rem', borderRadius: 8, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 700, cursor: 'pointer' }}
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clients table */}
      {loading ? (
        <div style={{ color: '#64748b', padding: '1rem' }}>{t('common.loading')}</div>
      ) : clients.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <User size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p>{t('partner.clients.noClients')}</p>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {[t('partner.clients.name'), t('partner.clients.email'), t('partner.clients.company'), t('partner.clients.phone'), t('partner.clients.createdAt'), ''].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map(client => (
                <tr key={client.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#0f172a' }}>{client.name}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#334155', fontSize: '0.87rem' }}>{client.email || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#334155', fontSize: '0.87rem' }}>{client.company || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#334155', fontSize: '0.87rem' }}>{client.phone || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.82rem' }}>{fmt(client.created_at)}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => openEdit(client)} style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: 7, padding: '0.3rem 0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.78rem', fontWeight: 700 }}>
                        <Edit2 size={13} /> {t('common.edit')}
                      </button>
                      <button onClick={() => handleDelete(client)} style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', borderRadius: 7, padding: '0.3rem 0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.78rem', fontWeight: 700 }}>
                        <Trash2 size={13} /> {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
